import { useState, type FormEvent } from 'react';
import { ApiError, errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import PasswordField from '../components/PasswordField';

/**
 * After this many failures in a row, stop repeating the same advice and tell
 * them who to ask. Somebody who has typed it wrong five times is not going to
 * get it right on the sixth by being told to check it again — by then the
 * likelier causes are an account that was never created, a password reset
 * nobody passed on, or the till pointing at the wrong API.
 */
const ATTEMPTS_BEFORE_ESCALATING = 5;

/**
 * What to put on screen for a failed sign-in.
 *
 * The server answers the same 401 for an unknown username, a wrong password
 * and an account with no password set — deliberately, so a guess never reveals
 * which half of it was right. That means this cannot say "no such user", and
 * should not pretend to: every one of those cases is genuinely "check both".
 *
 * What it can separate is everything that is NOT a credentials problem, which
 * is where a wrong message costs real time. A till that cannot reach the API
 * and a till being told the password is wrong look identical to the person
 * standing at it, and only one of them is worth retyping a password over.
 */
function signInError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isNetworkError) {
      return 'Cannot reach the server. Check this machine’s connection, then try again.';
    }
    switch (err.status) {
      case 400:
        return 'Enter both a username and a password.';
      case 401:
        return 'Wrong username or password. Check both, then try again.';
      case 429:
        return 'Too many attempts from this machine. Wait a minute, then try again.';
      default:
        if (err.status >= 500) {
          return 'The server is having trouble. Wait a moment, then try again.';
        }
    }
  }
  // Anything else carries its own message — the role check in AuthContext
  // throws one that explains itself better than a generic line would.
  return errorMessage(err, 'Could not sign in. Try again.');
}

export default function LoginScreen() {
  const { signIn, lockedOut } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // Starts with the reason, when the till was signed out because the Super
  // Admin took this role off it — otherwise the login screen appearing
  // mid-shift explains nothing.
  const [error, setError] = useState<string | null>(lockedOut);
  const [failures, setFailures] = useState(0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(username.trim(), password);
    } catch (err) {
      // The password is wrong until it is typed again — leaving it filled
      // invites submitting the same thing twice. The username stays: it is far
      // likelier to have been right.
      setPassword('');
      setFailures((n) => n + 1);
      setError(signInError(err));
      setBusy(false);
    }
  }

  const escalate = failures >= ATTEMPTS_BEFORE_ESCALATING;

  return (
    <div className="login">
      <div className="login-card">
        <h1>Blossom Bookfair</h1>
        <p className="login-sub">Billing counter</p>

        <form onSubmit={submit}>
          <label htmlFor="username">Username</label>
          <input
            id="username"
            className="input"
            // No autocapitalise and no autocorrect: a counter tablet's keyboard
            // will happily turn "rekha" into "Rekha" and hand the server a name
            // that does not exist, which reads here as a wrong password.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
          />

          <label htmlFor="password">Password</label>
          {/* The reveal matters here because a failed sign-in that was really a
              typo is indistinguishable from a wrong password — see signInError
              above, which cannot tell them apart either. */}
          <PasswordField id="password" value={password} onChange={setPassword} />

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={busy || !username.trim() || !password}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* One live region for both, so a screen reader announces the escalation
            as part of the same update rather than as a second, separate alert. */}
        {error && (
          <div role="alert">
            <p className="error">{error}</p>
            {escalate && (
              <p className="notice">
                That’s {failures} failed attempts. If you are sure the details
                are right, contact your development team — the account may not
                exist yet, or its password may have been reset.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
